import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Image,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
  VisuallyHidden,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconExternalLink, IconTrash } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { trackingUrl } from "@how/app-kit";
import { semanticColor, type SemanticColor } from "@how/design-system";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "@how/ui";
import {
  type CartLine,
  formatPrice,
  imageUrl,
  type MyOrder,
  type ShopProduct,
  useCancelMyOrder,
  useCatalog,
  useMyOrders,
  usePayOrder,
  usePlaceOrder,
  useStoreOptions,
} from "./shop-queries";
import { shippingFor, type Way, waysFor } from "./shop-rules";

/** An order's colors are the staff dashboard's (ORDER_STATUS_COLOR): the same status is never two colors. */
function statusOf(order: MyOrder): { label: string; color: SemanticColor } {
  const shipped = order.fulfilment === "ship";
  switch (order.status) {
    case "pending_payment":
      return { label: "Waiting for payment", color: semanticColor("warning") };
    case "paid":
      return {
        label: shipped
          ? "Paid: getting it ready to ship"
          : order.forEvent !== null
            ? "Paid: handed over at the event"
            : "Paid: pick it up at church",
        color: semanticColor("info"),
      };
    case "fulfilled":
      return {
        label: shipped ? "Shipped" : order.forEvent !== null ? "Handed over" : "Picked up",
        color: semanticColor("success"),
      };
    case "cancelled":
      return { label: "Cancelled", color: semanticColor("neutral") };
  }
}

/**
 * The Shop (ADR 0047): what the church sells, bought here and picked up at church or shipped, as the church offers.
 * The cart lives on this page; the order is placed, and its stock held, only when the member presses Pay. Paying
 * happens on Stripe's page, on the church's own account, which also asks for a shipping address.
 */
export function ShopPage() {
  const catalog = useCatalog();
  const options = useStoreOptions();
  const orders = useMyOrders();
  const place = usePlaceOrder();
  const [cart, setCart] = useState<CartLine[]>([]);
  const [chosenWay, setChosenWay] = useState<Way | null>(null);
  /** The last change to the cart, in words, for the status line a screen reader reads out (#261). */
  const [lastAction, setLastAction] = useState("");
  const [params, setParams] = useSearchParams();
  const paid = params.get("paid");

  useEffect(() => {
    if (paid === null) return;
    notifications.show(
      paid === "yes"
        ? {
            color: "teal",
            title: "Thank you",
            message: "Your order is paid. You'll find it under Your orders.",
          }
        : {
            color: "orange",
            title: "Not paid",
            message: "Your order is waiting. You can pay for it below.",
          },
    );
    setParams((current) => {
      current.delete("paid");
      current.delete("order");
      return current;
    });
  }, [paid, setParams]);

  const variants = useMemo(() => {
    const byId = new Map<string, { product: ShopProduct; name: string; priceCents: number }>();
    for (const p of catalog.data ?? [])
      for (const v of p.variants)
        byId.set(v.id, { product: p, name: v.name, priceCents: v.priceCents });
    return byId;
  }, [catalog.data]);
  const currency = catalog.data?.[0]?.currency ?? "usd";
  const itemsTotal = cart.reduce(
    (sum, l) => sum + (variants.get(l.variantId)?.priceCents ?? 0) * l.quantity,
    0,
  );
  const cartProducts = cart.flatMap((l) => {
    const v = variants.get(l.variantId);
    return v === undefined ? [] : [v.product];
  });
  const ways = options.data === undefined ? [] : waysFor(options.data, cartProducts);
  const way: Way | null =
    chosenWay !== null && ways.includes(chosenWay) ? chosenWay : (ways[0] ?? null);
  const shipping =
    way === "ship" && options.data !== undefined ? shippingFor(options.data, itemsTotal) : 0;
  const total = itemsTotal + shipping;
  const offersBoth = options.data?.pickup === true && options.data.shipping;
  const itemCount = cart.reduce((sum, l) => sum + l.quantity, 0);
  // Read out after each change (#261): the change, then the order as it stands, so the total is heard, not only seen.
  const cartStatus =
    lastAction === ""
      ? ""
      : cart.length === 0
        ? `${lastAction} Your order is empty.`
        : `${lastAction} Your order: ${String(itemCount)} ${itemCount === 1 ? "item" : "items"}, ${
            way === "ship"
              ? "shipped to you"
              : way === "pickup"
                ? "picked up at church"
                : "which cannot all come the same way"
          }, total ${formatPrice(total, currency)}.`;

  const add = (variantId: string, quantity: number) => {
    const added = variants.get(variantId);
    setLastAction(
      added === undefined
        ? "Added."
        : `Added ${String(quantity)} × ${added.product.name} (${added.name}).`,
    );
    setCart((current) => {
      const existing = current.find((l) => l.variantId === variantId);
      if (existing === undefined) return [...current, { variantId, quantity }];
      return current.map((l) =>
        l.variantId === variantId ? { ...l, quantity: Math.min(20, l.quantity + quantity) } : l,
      );
    });
  };

  return (
    <>
      <PageHeader
        title="Shop"
        description={
          options.data === undefined
            ? "Things from your church."
            : options.data.mode === "shopify"
              ? "Things from your church, sold in its own online store."
              : options.data.mode === "none"
                ? "Your church does not sell anything here."
                : options.data.shipping
                  ? options.data.pickup
                    ? "Things from your church, paid for here, picked up at church or shipped to you."
                    : "Things from your church, paid for here and shipped to you."
                  : "Things from your church, paid for here and picked up at church."
        }
      />
      {options.isPending ? (
        <LoadingState rows={3} />
      ) : options.data?.mode === "shopify" && options.data.shopifyUrl !== null ? (
        <Card withBorder>
          <Stack gap="sm" align="flex-start">
            <Text>Your church&apos;s store is on Shopify. You buy and pay there.</Text>
            <Button
              component="a"
              href={options.data.shopifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              rightSection={<IconExternalLink size={16} aria-hidden />}
            >
              Visit the store
            </Button>
          </Stack>
        </Card>
      ) : options.data?.mode === "none" ? (
        <EmptyState
          title="Nothing for sale here"
          description="Your church does not sell anything in the portal."
        />
      ) : (
        <>
          {catalog.isPending ? (
            <LoadingState rows={3} />
          ) : catalog.isError ? (
            <ErrorState message={catalog.error.message} onRetry={() => void catalog.refetch()} />
          ) : catalog.data.length === 0 ? (
            <EmptyState
              title="Nothing for sale right now"
              description="Your church has not added anything yet."
            />
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {catalog.data.map((p) => (
                <ProductCard key={p.id} product={p} onAdd={add} showWay={offersBoth} />
              ))}
            </SimpleGrid>
          )}

          {cart.length === 0 ? null : (
            <Card mt="lg" withBorder>
              <Stack gap="sm">
                <Title order={2} size="h3">
                  Your order
                </Title>
                {cart.map((l, index) => {
                  const v = variants.get(l.variantId);
                  if (v === undefined) return null;
                  return (
                    <Group key={l.variantId} justify="space-between" wrap="nowrap">
                      <Text size="sm">
                        {l.quantity} × {v.product.name} ({v.name})
                      </Text>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm" className="how-numeric">
                          {formatPrice(v.priceCents * l.quantity, v.product.currency)}
                        </Text>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Remove ${v.product.name} (${v.name})`}
                          data-remove-for={l.variantId}
                          onClick={() => {
                            // From the cart as it is now, not as this render saw it: two quick removals both
                            // count (#277).
                            setCart((current) =>
                              current.filter((x) => x.variantId !== l.variantId),
                            );
                            setLastAction(`Removed ${v.product.name} (${v.name}).`);
                            // The button leaves with its line, so focus goes to the next line's, or back to the
                            // product's own Add to order when the cart is empty, never to the page (#261).
                            requestAnimationFrame(() => {
                              const left = Array.from(
                                document.querySelectorAll<HTMLElement>("[data-remove-for]"),
                              );
                              const target =
                                left[Math.min(index, left.length - 1)] ??
                                document.querySelector<HTMLElement>(
                                  `[data-add-for="${v.product.id}"]`,
                                );
                              target?.focus();
                            });
                          }}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Group>
                  );
                })}
                {ways.length > 1 ? (
                  <SegmentedControl
                    aria-label="How you get your order"
                    value={way ?? "pickup"}
                    onChange={(value) => {
                      setChosenWay(value as Way);
                    }}
                    data={[
                      { value: "pickup", label: "Pick up at church" },
                      { value: "ship", label: "Ship to me" },
                    ]}
                  />
                ) : null}
                {way === "ship" ? (
                  <Group justify="space-between" wrap="nowrap">
                    <Text size="sm">Shipping</Text>
                    <Text size="sm" className="how-numeric">
                      {shipping === 0 ? "Free" : formatPrice(shipping, currency)}
                    </Text>
                  </Group>
                ) : null}
                {way === "ship" &&
                shipping > 0 &&
                options.data?.freeShippingOverCents !== null &&
                options.data?.freeShippingOverCents !== undefined ? (
                  <Text size="xs" c="dimmed">
                    Free shipping on orders of{" "}
                    {formatPrice(options.data.freeShippingOverCents, currency)} or more.
                  </Text>
                ) : null}
                {cart.length > 0 && options.data !== undefined && way === null ? (
                  <Alert color="orange" variant="light">
                    Some of these are picked up at church only and some are shipped only. Place them
                    as two orders.
                  </Alert>
                ) : null}
                {place.isError ? (
                  <Alert color="red" variant="light">
                    {place.error.message}
                  </Alert>
                ) : null}
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Held for you for a day while you pay.{" "}
                    {way === "ship"
                      ? "Stripe's checkout asks for your address."
                      : (options.data?.pickupNote ?? "Picked up at church.")}
                    {options.data?.tax === true ? " Sales tax is added at checkout." : ""}
                  </Text>
                  <Button
                    loading={place.isPending}
                    disabled={total < 50 || way === null}
                    onClick={() => {
                      if (way !== null) place.mutate({ lines: cart, fulfilment: way });
                    }}
                  >
                    Pay {formatPrice(total, currency)}
                  </Button>
                </Group>
              </Stack>
            </Card>
          )}
        </>
      )}

      <VisuallyHidden role="status" aria-live="polite">
        {cartStatus}
      </VisuallyHidden>

      <Title order={2} size="h3" mt="xl" mb="sm">
        Your orders
      </Title>
      {orders.isPending ? (
        <LoadingState rows={2} />
      ) : orders.isError ? (
        <ErrorState message={orders.error.message} onRetry={() => void orders.refetch()} />
      ) : orders.data.length === 0 ? (
        <Text size="sm" c="dimmed">
          No orders yet.
        </Text>
      ) : (
        <Stack gap="sm">
          {orders.data.map((o) => (
            <OrderCard key={o.id} order={o} />
          ))}
        </Stack>
      )}
    </>
  );
}

function ProductCard({
  product,
  onAdd,
  showWay,
}: {
  readonly product: ShopProduct;
  readonly onAdd: (variantId: string, quantity: number) => void;
  /** The church offers both pickup and shipping, so a product limited to one says so. */
  readonly showWay: boolean;
}) {
  const first = product.variants.find((v) => v.available) ?? product.variants[0];
  const [variantId, setVariantId] = useState<string | null>(first?.id ?? null);
  const [quantity, setQuantity] = useState(1);
  const chosen = product.variants.find((v) => v.id === variantId) ?? null;
  const soldOut = product.variants.every((v) => !v.available);

  return (
    <Card withBorder padding="md">
      {product.image === null ? null : (
        <Card.Section mb="sm">
          <Image src={imageUrl(product.image.path)} alt={product.image.alt} h={180} fit="cover" />
        </Card.Section>
      )}
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          {/* A heading, so a screen reader can move product to product (owner, 2026-09-30, #285); same look. */}
          <Text component="h2" fw={600} m={0}>
            {product.name}
          </Text>
          {chosen === null ? null : (
            <Text fw={600} className="how-numeric">
              {formatPrice(chosen.priceCents, product.currency)}
            </Text>
          )}
        </Group>
        {product.description === null ? null : (
          <Text size="sm" c="dimmed">
            {product.description}
          </Text>
        )}
        {showWay && product.fulfilment !== "either" ? (
          <Text size="xs" c="dimmed">
            {product.fulfilment === "pickup" ? "Picked up at church only" : "Shipped only"}
          </Text>
        ) : null}
        {product.variants.length > 1 ? (
          <Select
            label="Option"
            aria-label={`Option for ${product.name}`}
            value={variantId}
            onChange={setVariantId}
            allowDeselect={false}
            data={product.variants.map((v) => ({
              value: v.id,
              label: v.available ? v.name : `${v.name} (sold out)`,
              disabled: !v.available,
            }))}
          />
        ) : null}
        {chosen !== null && chosen.available && typeof chosen.left === "number" ? (
          <Badge variant="light" color="orange" w="fit-content">
            {chosen.left} left
          </Badge>
        ) : null}
        {soldOut ? (
          <Badge variant="light" color="gray" w="fit-content">
            Sold out
          </Badge>
        ) : (
          <Group gap="xs" align="flex-end" wrap="nowrap">
            <NumberInput
              label="How many"
              aria-label={`How many ${product.name}`}
              min={1}
              max={Math.min(20, typeof chosen?.left === "number" ? chosen.left : 20)}
              allowDecimal={false}
              value={quantity}
              onChange={(value) => {
                setQuantity(typeof value === "number" && value >= 1 ? value : 1);
              }}
              w={110}
            />
            <Button
              variant="light"
              aria-label={`Add to order: ${product.name}`}
              data-add-for={product.id}
              disabled={chosen === null || !chosen.available}
              onClick={() => {
                if (chosen !== null) onAdd(chosen.id, quantity);
              }}
            >
              Add to order
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

function OrderCard({ order }: { readonly order: MyOrder }) {
  const pay = usePayOrder();
  const cancel = useCancelMyOrder();
  const status = statusOf(order);
  const error = pay.error ?? cancel.error;
  const charged = order.totalCents + order.taxCents;
  const link = trackingUrl(order.carrier, order.trackingNumber);
  const to = order.shipTo;
  return (
    <Card withBorder padding="sm">
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" fw={600}>
            Order #{order.number} · {formatPrice(charged, order.currency)}
          </Text>
          <Group gap={4}>
            <Badge variant="light" color={status.color}>
              {status.label}
            </Badge>
            {order.refundedCents > 0 ? (
              <Badge variant="light" color="gray">
                {order.refundedCents >= charged
                  ? "Refunded"
                  : `${formatPrice(order.refundedCents, order.currency)} refunded`}
              </Badge>
            ) : null}
          </Group>
        </Group>
        {order.forEvent === null ? null : <Text size="sm">Add-ons for {order.forEvent}</Text>}
        <Text size="sm" c="dimmed">
          {order.items.map((i) => `${String(i.quantity)} × ${i.product} (${i.variant})`).join(", ")}
          {order.fulfilment === "ship"
            ? `, shipping ${order.shippingCents === 0 ? "free" : formatPrice(order.shippingCents, order.currency)}`
            : ""}
          {order.taxCents > 0 ? `, tax ${formatPrice(order.taxCents, order.currency)}` : ""}
        </Text>
        {order.fulfilment === "ship" && to !== null ? (
          <Text size="sm">
            To {[to.name, to.line1, to.city, to.state].filter(Boolean).join(", ")}
          </Text>
        ) : null}
        {order.status === "fulfilled" && order.fulfilment === "ship" ? (
          <Text size="sm">
            {order.carrier ?? "Shipped"}
            {order.trackingNumber === null ? "" : ": "}
            {link === null ? (
              order.trackingNumber
            ) : (
              <Anchor
                href={link}
                target="_blank"
                rel="noreferrer"
                aria-label={`Track ${order.trackingNumber ?? ""} with ${order.carrier ?? "the carrier"} (opens in a new tab)`}
              >
                Track {order.trackingNumber}
              </Anchor>
            )}
          </Text>
        ) : null}
        {error ? (
          <Alert color="red" variant="light">
            {error.message}
          </Alert>
        ) : null}
        {order.status === "pending_payment" ? (
          <Group gap="xs">
            <Button
              size="compact-sm"
              loading={pay.isPending}
              onClick={() => {
                pay.mutate(order.id);
              }}
            >
              Pay {formatPrice(order.totalCents, order.currency)}
            </Button>
            <Button
              size="compact-sm"
              variant="subtle"
              color="red"
              loading={cancel.isPending}
              onClick={() => {
                cancel.mutate(order.id);
              }}
            >
              Cancel order
            </Button>
          </Group>
        ) : null}
      </Stack>
    </Card>
  );
}
